import { useState, useEffect, useRef } from "react";
import { api } from "../lib/api";
import { Avatar } from "./Avatar";
import styles from "./AddMemberDialog.module.css";

/** Mask email for GDPR: "finn.backs21@gmail.com" → "f***@g***.com" */
function maskEmail(email: string): string {
  const [local, domain] = email.split("@");
  if (!local || !domain) return "***";
  const domainParts = domain.split(".");
  const tld = domainParts.pop() ?? "";
  const domainName = domainParts.join(".");
  return `${local[0]}***@${domainName[0]}***.${tld}`;
}

interface User {
  id: string;
  email: string;
  displayName: string;
}

interface AddMemberDialogProps {
  onClose: () => void;
  onSelect: (user: User) => void;
  title?: string;
  description?: string;
}

export function AddMemberDialog({
  onClose,
  onSelect,
  title = "Person hinzufügen",
  description = "Name oder E-Mail eingeben",
}: AddMemberDialogProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (query.length < 2) {
      setResults([]);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const data = await api.get<{ users: User[] }>(
          `/users/search?q=${encodeURIComponent(query)}`,
        );
        setResults(data.users);
      } catch {
        setError("Suche fehlgeschlagen");
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
        <h3 className={styles.title}>{title}</h3>
        <p className={styles.description}>{description}</p>

        <input
          ref={inputRef}
          type="text"
          className={styles.searchInput}
          placeholder="Suchen..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />

        <div className={styles.results}>
          {loading && <div className={styles.status}>Suche...</div>}
          {error && <div className={styles.error}>{error}</div>}
          {!loading && query.length >= 2 && results.length === 0 && (
            <div className={styles.status}>Keine Ergebnisse</div>
          )}
          {results.map((user) => (
            <button
              key={user.id}
              className={styles.userItem}
              onClick={() => onSelect(user)}
            >
              <Avatar name={user.displayName} size="sm" />
              <div className={styles.userInfo}>
                <span className={styles.userName}>{user.displayName}</span>
                <span className={styles.userEmail}>{maskEmail(user.email)}</span>
              </div>
            </button>
          ))}
        </div>

        <div className={styles.actions}>
          <button className={styles.cancelBtn} onClick={onClose}>
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
}
